import React, { useState, useEffect } from 'react';
import { fm } from '../utils';

const API = process.env.REACT_APP_API_URL || '/api';

const UrunModifierSheet = ({ urun, onClose, onEkle }) => {
  const [groups, setGroups] = useState([]);
  const [secimler, setSecimler] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchModifiers = async () => {
      try {
        const res = await fetch(`${API}/productmodifiers/urun/${urun.urunID}`);
        if (!res.ok) throw new Error('Yüklenemedi');
        const data = await res.json();
        data.sort((a, b) => (a.sira || 0) - (b.sira || 0));
        setGroups(data);
        const defaults = {};
        data.forEach(g => {
          if (g.maxSecim === 1 && g.items?.length > 0) {
            defaults[g.id] = [g.items[0].id];
          } else if (g.ad === 'Top Sayısı' && g.minSecim > 0 && g.items?.length > 0) {
            defaults[g.id] = Array(g.minSecim).fill(g.items[0].id);
          } else {
            defaults[g.id] = [];
          }
        });
        setSecimler(defaults);
      } catch (e) {
        setError(e.message);
      }
      setLoading(false);
    };
    fetchModifiers();
  }, [urun.urunID]);

  const currentGroup = groups[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === groups.length - 1;

  const countOf = (groupId, itemId) => (secimler[groupId] || []).filter(id => id === itemId).length;
  const totalCount = (groupId) => (secimler[groupId] || []).length;

  const topSayisiGroup = groups.find(g => g.ad === 'Top Sayısı');
  const aromaGroup = groups.find(g => g.ad === 'Aromalar');
  const isTopSayisiStep = currentGroup?.id === topSayisiGroup?.id;
  const isAromaStep = currentGroup?.id === aromaGroup?.id;

  const topIlkItem = topSayisiGroup?.items?.[0];
  const topBirimFiyat = topIlkItem?.ekFiyat || 0;
  const topMin = topSayisiGroup?.minSecim || 0;
  const topMax = topSayisiGroup?.maxSecim || 20;
  const topMiktar = topSayisiGroup ? totalCount(topSayisiGroup.id) : 0;
  const topToplamFiyat = topMiktar * topBirimFiyat;

  const topAdet = (() => {
    if (!topSayisiGroup || !aromaGroup) return 0;
    return topMiktar;
  })();

  const getEffectiveMax = (groupId) => {
    if (groupId === aromaGroup?.id && topAdet > 0) return topAdet;
    const group = groups.find(g => g.id === groupId);
    return group?.maxSecim || 0;
  };

  const toggleSecim = (groupId, itemId) => {
    setSecimler(prev => {
      const group = groups.find(g => g.id === groupId);
      const current = [...(prev[groupId] || [])];
      const isSelected = current.includes(itemId);
      if (isSelected) return { ...prev, [groupId]: current.filter(id => id !== itemId) };
      if (group?.maxSecim === 1) return { ...prev, [groupId]: [itemId] };
      const max = getEffectiveMax(groupId);
      if (max > 0 && current.length >= max) return prev;
      return { ...prev, [groupId]: [...current, itemId] };
    });
  };

  const topArtir = () => {
    if (!topSayisiGroup || !topIlkItem) return;
    setSecimler(prev => {
      const current = [...(prev[topSayisiGroup.id] || [])];
      if (current.length >= topMax) return prev;
      return { ...prev, [topSayisiGroup.id]: [...current, topIlkItem.id] };
    });
  };

  const topAzalt = () => {
    if (!topSayisiGroup || !topIlkItem) return;
    setSecimler(prev => {
      const current = [...(prev[topSayisiGroup.id] || [])];
      const idx = current.lastIndexOf(topIlkItem.id);
      if (idx === -1) return prev;
      current.splice(idx, 1);
      return { ...prev, [topSayisiGroup.id]: current };
    });
  };

  const artir = (groupId, itemId) => {
    setSecimler(prev => {
      const current = [...(prev[groupId] || [])];
      const max = getEffectiveMax(groupId);
      if (max > 0 && current.length >= max) return prev;
      return { ...prev, [groupId]: [...current, itemId] };
    });
  };

  const azalt = (groupId, itemId) => {
    setSecimler(prev => {
      const current = [...(prev[groupId] || [])];
      const idx = current.lastIndexOf(itemId);
      if (idx === -1) return prev;
      current.splice(idx, 1);
      return { ...prev, [groupId]: current };
    });
  };

  const modifierFiyat = groups.reduce((toplam, g) => {
    return toplam + (g.items || []).reduce((t, i) => {
      return t + countOf(g.id, i.id) * (i.ekFiyat || 0);
    }, 0);
  }, 0);

  const toplamFiyat = (urun.fiyat || 0) + modifierFiyat;

  const stepValid = () => {
    if (!currentGroup) return false;
    if (isTopSayisiStep) return topMiktar >= topMin && topMiktar <= topMax;
    const adet = totalCount(currentGroup.id);
    if (currentGroup.zorunlu && adet === 0) return false;
    if (currentGroup.minSecim > 0 && adet < currentGroup.minSecim) return false;
    const max = getEffectiveMax(currentGroup.id);
    if (max > 0 && adet > max) return false;
    if (isAromaStep && topMiktar > 0 && adet !== topMiktar) return false;
    return true;
  };

  const validateAll = () => {
    for (const g of groups) {
      if (g.id === topSayisiGroup?.id) {
        if (topMiktar < topMin) return `En az ${topMin} top seçmelisiniz.`;
        if (topMiktar > topMax) return `En fazla ${topMax} top seçebilirsiniz.`;
        continue;
      }
      const adet = totalCount(g.id);
      const max = getEffectiveMax(g.id);
      if (g.zorunlu && adet === 0) return `${g.ad} seçimi zorunludur.`;
      if (g.minSecim > 0 && adet < g.minSecim) return `${g.ad} için en az ${g.minSecim} seçim yapmalısınız.`;
      if (max > 0 && adet > max) return `${g.ad} için en fazla ${max} seçim yapabilirsiniz.`;
      if (g.id === aromaGroup?.id && topMiktar > 0 && adet !== topMiktar) {
        return `${topMiktar} top seçtiniz, ${topMiktar} adet aroma seçmelisiniz. (Şu an: ${adet})`;
      }
    }
    return null;
  };

  const ileri = () => {
    if (!currentGroup || !stepValid()) {
      setError(currentGroup ? `Lütfen "${currentGroup.ad}" için seçim yapın.` : 'Modifier bilgisi alınamadı.');
      return;
    }
    setError(null);
    if (isLastStep) {
      const hata = validateAll();
      if (hata) { setError(hata); return; }
      const modifierSecimleri = [];
      const modifierDetaylari = [];
      groups.forEach(g => {
        const arr = secimler[g.id] || [];
        const counts = {};
        arr.forEach(id => { counts[id] = (counts[id] || 0) + 1; });
        Object.entries(counts).forEach(([itemIdStr, qty]) => {
          const itemId = Number(itemIdStr);
          const item = g.items?.find(i => i.id === itemId);
          if (item) {
            modifierSecimleri.push({ modifierGroupId: g.id, modifierItemId: item.id, qty });
            modifierDetaylari.push({
              modifierGroupId: g.id,
              modifierItemId: item.id,
              modifierGroupAdi: g.ad,
              modifierItemAdi: item.ad,
              ekFiyat: item.ekFiyat,
              qty
            });
          }
        });
      });
      onEkle(urun, modifierSecimleri, modifierDetaylari, toplamFiyat);
      onClose();
    } else {
      setCurrentStep(s => s + 1);
    }
  };

  const geri = () => {
    setError(null);
    setCurrentStep(s => Math.max(0, s - 1));
  };

  const currentMax = getEffectiveMax(currentGroup?.id);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
        <div className="relative w-full bg-white dark:bg-surface-800 rounded-t-2xl shadow-2xl animate-slide-up p-5 pb-8 flex items-center justify-center" onClick={e => e.stopPropagation()}>
          <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full bg-white dark:bg-surface-800 rounded-t-2xl shadow-2xl animate-slide-up max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-surface-600 mx-auto mt-3 mb-2" />
        <div className="px-5 pb-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-surface-800 dark:text-white text-base">{urun.urunAdi}</h3>
              <p className="text-xs text-surface-400">Özelleştirin ve sepete ekleyin</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-surface-700 text-gray-400 touch-manipulation">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Empty state */}
          {!error && groups.length === 0 && (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-surface-700 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <p className="text-sm font-medium text-surface-600 dark:text-surface-400 mb-1">Bu ürün için modifier tanımlanmamış</p>
              <p className="text-[11px] text-surface-400 mb-4">Admin panelden ürün eşlemesini kontrol edin</p>
              <button onClick={onClose}
                className="px-6 py-2.5 text-sm font-semibold bg-gray-100 dark:bg-surface-700 text-gray-500 dark:text-surface-400 rounded-xl hover:bg-gray-200 dark:hover:bg-surface-600 transition-all touch-manipulation">Kapat</button>
            </div>
          )}

          {/* Step indicator */}
          {groups.length > 1 && (
            <>
              <div className="flex items-center gap-1.5 mb-4 px-1">
                {groups.map((g, i) => {
                  const dolu = g.zorunlu ? (g.id === topSayisiGroup?.id ? topMiktar > 0 : totalCount(g.id) > 0) : true;
                  const aktif = i === currentStep;
                  const gecildi = i < currentStep;
                  return (
                    <button key={g.id} onClick={() => { if (i < currentStep) setCurrentStep(i); }}
                      className={`flex-1 h-1.5 rounded-full transition-all ${
                        aktif ? 'bg-emerald-500 h-2' : gecildi ? 'bg-emerald-300 cursor-pointer' : dolu ? 'bg-gray-300 dark:bg-surface-600' : 'bg-gray-200 dark:bg-surface-700'
                      }`} />
                  );
                })}
              </div>
              <div className="flex items-center gap-1.5 mb-4">
                {groups.map((g, i) => (
                  <span key={g.id} className={`flex-1 text-[9px] font-medium text-center truncate ${
                    i === currentStep ? 'text-emerald-600 dark:text-emerald-400' : i < currentStep ? 'text-emerald-400' : 'text-gray-400'
                  }`}>{g.ad}</span>
                ))}
              </div>
            </>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
              </div>
            </div>
          )}

          {/* Quick combos on first step */}
          {isFirstStep && <QuickCombos urun={urun} groups={groups} onSec={setSecimler} />}

          {/* Current group */}
          {currentGroup && (
            <div key={currentGroup.id} className="mb-5 animate-fade-in">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-surface-800 dark:text-white">
                  {currentGroup.ad}
                  {currentGroup.zorunlu && <span className="text-red-400 ml-0.5">*</span>}
                </h4>
                {isTopSayisiStep ? (
                  <span className={`text-[11px] font-medium ${topMiktar > 0 ? 'text-emerald-500' : 'text-surface-400'}`}>
                    {topMiktar} adet
                  </span>
                ) : currentGroup.maxSecim === 1 ? (
                  <span className="text-[11px] text-surface-400 font-medium">Tek seçim</span>
                ) : (
                  <span className={`text-[11px] font-medium ${totalCount(currentGroup.id) > 0 ? 'text-emerald-500' : 'text-surface-400'}`}>
                    {totalCount(currentGroup.id)}{currentMax > 0 ? ` / ${currentMax}` : ''} seçili
                  </span>
                )}
              </div>

              {/* Top Sayısı counter */}
              {isTopSayisiStep && (
                <>
                  <p className="text-[10px] text-surface-400 mb-4 -mt-2">
                    {topMin === topMax ? `${topMin} top` : `${topMin}-${topMax} top arası seçin`}
                  </p>
                  <div className="flex items-center justify-center gap-5 py-4">
                    <button onClick={topAzalt} disabled={topMiktar <= 0}
                      className={`w-11 h-11 rounded-xl flex items-center justify-center text-2xl font-bold transition-all active:scale-90 touch-manipulation ${
                        topMiktar > 0
                          ? 'bg-white dark:bg-surface-700 border-2 border-gray-200 dark:border-surface-600 text-surface-600 dark:text-surface-400'
                          : 'bg-gray-50 dark:bg-surface-700/30 border-2 border-gray-100 dark:border-surface-700 text-gray-300 dark:text-surface-600 cursor-not-allowed'
                      }`}>−</button>
                    <div className="text-center min-w-[60px]">
                      <span className="text-3xl font-bold text-surface-800 dark:text-white tabular-nums">{topMiktar}</span>
                      {topBirimFiyat > 0 && (
                        <p className="text-[10px] text-surface-400 mt-1">{fm(topBirimFiyat)} / top</p>
                      )}
                    </div>
                    <button onClick={topArtir} disabled={topMiktar >= topMax}
                      className={`w-11 h-11 rounded-xl flex items-center justify-center text-2xl font-bold transition-all active:scale-90 touch-manipulation ${
                        topMiktar < topMax
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                          : 'bg-gray-100 dark:bg-surface-700 text-gray-300 dark:text-surface-600 cursor-not-allowed'
                      }`}>+</button>
                  </div>
                  {topMiktar >= topMin && topBirimFiyat > 0 && (
                    <p className="text-center text-xs font-medium text-surface-500">
                      {topMiktar} top × {fm(topBirimFiyat)} = <span className="text-emerald-600 dark:text-emerald-400 font-bold">{fm(topToplamFiyat)}</span>
                    </p>
                  )}
                </>
              )}

              {/* Aromalar grid */}
              {isAromaStep && !isTopSayisiStep && (
                <>
                  {topAdet > 0 && (
                    <p className="text-[10px] text-surface-400 mb-2.5 -mt-2">
                      {topAdet} top seçtiniz. En fazla {topAdet} adet aroma seçebilirsiniz.
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    {currentGroup.items?.filter(i => i.aktif !== false).map(i => {
                      const adet = countOf(currentGroup.id, i.id);
                      return (
                        <div key={i.id}
                          className={`flex flex-col items-center justify-between px-2 py-3 min-h-[110px] text-sm rounded-xl border-2 transition-all ${
                            adet > 0
                              ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500'
                              : 'bg-white dark:bg-surface-700 border-gray-200 dark:border-surface-600'
                          }`}>
                          <span className={`text-xs font-semibold text-center leading-tight ${adet > 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-surface-700 dark:text-surface-300'}`}>
                            {i.ad}
                          </span>
                          <span className="text-[10px] font-medium text-emerald-500 dark:text-emerald-400">
                            {i.ekFiyat > 0 ? `+${fm(i.ekFiyat)}` : '\u00A0'}
                          </span>
                          <div className="flex items-center gap-2">
                            <button onClick={() => azalt(currentGroup.id, i.id)} disabled={adet === 0}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center text-base font-bold transition-all touch-manipulation ${
                                adet > 0
                                  ? 'bg-white dark:bg-surface-700 border border-gray-200 dark:border-surface-600 text-surface-600 dark:text-surface-400 active:scale-90'
                                  : 'bg-gray-50 dark:bg-surface-700/30 text-gray-300 dark:text-surface-600 cursor-not-allowed'
                              }`}>−</button>
                            <span className="w-5 text-center text-sm font-bold tabular-nums text-surface-800 dark:text-white">{adet}</span>
                            <button onClick={() => artir(currentGroup.id, i.id)} disabled={currentMax > 0 && totalCount(currentGroup.id) >= currentMax}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center text-base font-bold transition-all touch-manipulation ${
                                currentMax > 0 && totalCount(currentGroup.id) >= currentMax
                                  ? 'bg-gray-50 dark:bg-surface-700/30 text-gray-300 dark:text-surface-600 cursor-not-allowed'
                                  : 'bg-emerald-500 text-white active:scale-90'
                              }`}>+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Other groups (Sunum Tipi, etc.) */}
              {!isTopSayisiStep && !isAromaStep && (
                <>
                  {currentMax > 0 && currentGroup.items?.length > 0 && (
                    <p className="text-[10px] text-surface-400 mb-2.5 -mt-2">
                      {currentGroup.minSecim > 1 && `En az ${currentGroup.minSecim}, `}en fazla {currentMax} adet seçebilirsiniz
                    </p>
                  )}
                  <div className="flex flex-col gap-2">
                    {currentGroup.items?.filter(i => i.aktif !== false).map(i => {
                      const adet = countOf(currentGroup.id, i.id);
                      if (currentGroup.maxSecim === 1) {
                        return (
                          <button key={i.id} onClick={() => toggleSecim(currentGroup.id, i.id)}
                            className={`w-full flex items-center justify-between px-4 py-3 text-sm font-semibold rounded-xl border-2 transition-all active:scale-[0.97] touch-manipulation ${
                              adet > 0
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500 text-emerald-700 dark:text-emerald-300 shadow-sm'
                                : 'bg-white dark:bg-surface-700 border-gray-200 dark:border-surface-600 text-surface-600 dark:text-surface-400 hover:border-emerald-300 dark:hover:border-emerald-600'
                            }`}>
                            <span>{i.ad}</span>
                            {i.ekFiyat > 0 && (
                              <span className="text-[10px] font-medium text-emerald-500 dark:text-emerald-400">+{fm(i.ekFiyat)}</span>
                            )}
                          </button>
                        );
                      }
                      return (
                        <div key={i.id}
                          className={`flex items-center justify-between px-4 py-2.5 text-sm rounded-xl border-2 transition-all ${
                            adet > 0
                              ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500'
                              : 'bg-white dark:bg-surface-700 border-gray-200 dark:border-surface-600'
                          }`}>
                          <div className="flex-1 min-w-0">
                            <span className={`font-semibold ${adet > 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-surface-600 dark:text-surface-400'}`}>{i.ad}</span>
                            {i.ekFiyat > 0 && (
                              <span className="block text-[10px] font-medium text-emerald-500 dark:text-emerald-400">+{fm(i.ekFiyat)} / adet</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-3">
                            <button onClick={() => azalt(currentGroup.id, i.id)} disabled={adet === 0}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg font-bold transition-all touch-manipulation ${
                                adet > 0
                                  ? 'bg-white dark:bg-surface-700 border border-gray-200 dark:border-surface-600 text-surface-600 dark:text-surface-400 active:scale-90'
                                  : 'bg-gray-50 dark:bg-surface-700/30 text-gray-300 dark:text-surface-600 cursor-not-allowed'
                              }`}>−</button>
                            <span className="w-6 text-center text-sm font-bold tabular-nums text-surface-800 dark:text-white">{adet}</span>
                            <button onClick={() => artir(currentGroup.id, i.id)} disabled={currentMax > 0 && totalCount(currentGroup.id) >= currentMax}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg font-bold transition-all touch-manipulation ${
                                currentMax > 0 && totalCount(currentGroup.id) >= currentMax
                                  ? 'bg-gray-50 dark:bg-surface-700/30 text-gray-300 dark:text-surface-600 cursor-not-allowed'
                                  : 'bg-emerald-500 text-white active:scale-90'
                              }`}>+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Total */}
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="text-xs font-medium text-surface-400">Toplam</span>
            <span className="text-lg font-bold text-surface-800 dark:text-white">{fm(toplamFiyat)}</span>
          </div>

          {/* Navigation */}
          <div className="flex gap-2">
            {isFirstStep ? (
              <button onClick={onClose}
                className="flex-1 py-3 text-sm font-semibold bg-gray-100 dark:bg-surface-700 text-gray-500 dark:text-surface-400 rounded-xl hover:bg-gray-200 dark:hover:bg-surface-600 transition-all touch-manipulation">İptal</button>
            ) : (
              <button onClick={geri}
                className="flex-1 py-3 text-sm font-semibold bg-gray-100 dark:bg-surface-700 text-gray-500 dark:text-surface-400 rounded-xl hover:bg-gray-200 dark:hover:bg-surface-600 transition-all touch-manipulation">Geri</button>
            )}
            <button onClick={ileri}
              className="flex-[2] py-3 text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.97] touch-manipulation">
              {isLastStep ? 'Sepete Ekle' : 'Devam'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuickCombos = ({ urun, groups, onSec }) => {
  const [combos, setCombos] = useState([]);

  useEffect(() => {
    if (!urun?.urunID) return;
    fetch(`${API}/hizlikombinasyon/urun/${urun.urunID}`)
      .then(r => r.json())
      .then(setCombos)
      .catch(() => {});
  }, [urun?.urunID]);

  if (combos.length === 0) return null;

  const applyCombo = (combo) => {
    let secimler;
    try { secimler = JSON.parse(combo.modifierSecimleri || '[]'); } catch { return; }
    const yeniSecimler = {};
    groups.forEach(g => { yeniSecimler[g.id] = []; });
    secimler.forEach(s => {
      const item = groups.flatMap(g => g.items || []).find(i => i.id === s.modifierItemId);
      if (item) {
        const gId = item.modifierGroupId;
        yeniSecimler[gId] = [...(yeniSecimler[gId] || []), item.id];
      }
    });
    onSec(yeniSecimler);
  };

  return (
    <div className="mb-4">
      <p className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Hızlı Seç</p>
      <div className="flex flex-wrap gap-1.5">
        {combos.map(c => (
          <button key={c.id} onClick={() => applyCombo(c)}
            className="px-3 py-1.5 text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all active:scale-[0.97] touch-manipulation">
            {c.ad}
          </button>
        ))}
      </div>
    </div>
  );
};

export default UrunModifierSheet;
